import React, { useMemo, useState } from 'react';
import { Channel, Message, User, handleAvatarError } from '../types';
import { resolveAvatarUrl } from '../utils/serverUrl';
import { ChannelTypeIcon, channelDisplayName } from './ChannelListItem';
import { matchesChannelQuery } from '../utils/channelSearch';
import { useChannelMemberIndex } from '../hooks/useChannelMemberIndex';
import { Search, X, Forward, Check } from 'lucide-react';

/**
 * 메시지 전달 대상 채널 선택 모달 (Phase 2)
 * 소스는 App의 channels state(= 요청자 접근 가능 채널). public은 관리자에게만 노출
 * (관리자만 말하기 정책 — 최종 검증은 서버 POST /api/messages/forward).
 * 체크박스 다중 선택(상한 10) → "전달 (n)" 확정.
 */

const MAX_TARGETS = 10;

interface ForwardTargetModalProps {
  sourceMessages: Message[];
  channels: Channel[];
  currentUser: User;
  onConfirm: (targetChannelIds: number[]) => void;
  onClose: () => void;
  /** 다중 선택 모드로 전환 (단건 진입 시에만 표시) */
  onSelectMore?: () => void;
  /** 같은 창을 일정 '대화방에 공유'에도 쓴다 — 제목·버튼·미리보기 문구만 바꾼다 */
  title?: string;
  confirmLabel?: string;
  previewText?: string;
}

export const ForwardTargetModal: React.FC<ForwardTargetModalProps> = ({
  sourceMessages,
  channels,
  currentUser,
  onConfirm,
  onClose,
  onSelectMore,
  title = '메시지 전달',
  confirmLabel = '전달',
  previewText: previewOverride,
}) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // 참여자 이름 검색용 인덱스 — 이 모달은 열릴 때만 마운트되므로 항상 활성.
  // 대화방 목록과 모듈 캐시를 공유해 실제 요청은 세션당 1회다
  const memberIndex = useChannelMemberIndex(true);

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    return channels
      .filter(ch => !ch.isArchived)
      .filter(ch => ch.type !== 'public' || currentUser.isAdmin === true)
      // 검색 기준(이름·참여자·설명)은 대화방 목록과 동일한 매처를 쓴다 — 두 화면이 갈라지지 않게
      .filter(ch => matchesChannelQuery(ch, q, memberIndex))
      .sort((a, b) => (b.lastMessageAt || '').localeCompare(a.lastMessageAt || ''));
  }, [channels, currentUser.isAdmin, query, memberIndex]);

  const toggle = (channelId: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(channelId)) {
        next.delete(channelId);
      } else {
        if (next.size >= MAX_TARGETS) return prev; // 상한 초과 무시
        next.add(channelId);
      }
      return next;
    });
  };

  const previewText = previewOverride ?? (sourceMessages.length === 1
    ? (sourceMessages[0].text && sourceMessages[0].text !== '파일을 공유했습니다.'
        ? sourceMessages[0].text
        : '첨부파일')
    : `메시지 ${sourceMessages.length}개`);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-md max-h-[80vh] bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden">
        {/* 헤더 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Forward size={18} className="text-brand-600 shrink-0" />
            <div className="min-w-0">
              <h2 className="text-base font-bold text-slate-800">{title}</h2>
              <p className="text-xs text-slate-400 truncate">{previewText}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 -mr-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors shrink-0"
            title="닫기"
          >
            <X size={18} />
          </button>
        </div>

        {/* 검색 */}
        <div className="px-4 py-2 border-b border-slate-100 shrink-0">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="이름, 참여자, 설명으로 찾기"
              aria-label="이름, 참여자, 설명으로 대화방 찾기"
              className="w-full pl-9 pr-8 py-2 bg-slate-100 rounded-lg text-base md:text-sm text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-600"
                title="지우기"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* 채널 목록 */}
        <div className="flex-1 overflow-y-auto">
          {candidates.length === 0 ? (
            <div className="flex items-center justify-center h-32 text-sm text-slate-400">
              전달할 수 있는 채팅방이 없습니다
            </div>
          ) : (
            candidates.map(ch => {
              const isChecked = selected.has(ch.id);
              return (
                <button
                  key={ch.id}
                  onClick={() => toggle(ch.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    isChecked ? 'bg-brand-50/60' : 'hover:bg-slate-50'
                  }`}
                >
                  {ch.type === 'dm' && ch.dmPartnerAvatar ? (
                    <img
                      src={resolveAvatarUrl(ch.dmPartnerAvatar)} onError={handleAvatarError}
                      alt=""
                      className="w-9 h-9 rounded-full object-cover shrink-0"
                    />
                  ) : (
                    <ChannelTypeIcon type={ch.type} size="sm" />
                  )}
                  <span className="flex-1 min-w-0 text-sm text-slate-800 truncate">
                    {channelDisplayName(ch)}
                  </span>
                  <span
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${
                      isChecked ? 'bg-brand-600 border-brand-600 text-white' : 'border-slate-300 text-transparent'
                    }`}
                  >
                    <Check size={12} strokeWidth={3} />
                  </span>
                </button>
              );
            })
          )}
        </div>

        {/* 하단 액션 */}
        <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-100 shrink-0">
          {onSelectMore && (
            <button
              onClick={onSelectMore}
              className="px-3 py-2 text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            >
              여러 개 선택
            </button>
          )}
          <div className="flex-1" />
          {selected.size >= MAX_TARGETS && (
            <span className="text-xs text-amber-600">최대 {MAX_TARGETS}개</span>
          )}
          <button
            onClick={() => onConfirm(Array.from(selected))}
            disabled={selected.size === 0}
            className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {confirmLabel} ({selected.size})
          </button>
        </div>
      </div>
    </div>
  );
};
